import axios from 'axios';
import { useEffect, useState } from 'react';
import { ContentCard } from '../../components/ContentCard/ContentCard';
import { Button } from '../../components/Form/Button';
import { AddProductModal } from '../../components/editOrder/addProduct/AddProductModal';
import { SubscriptionListItem } from '../../components/subscriptions/SubscriptionListItem';
import { WITH_CREDENTIALS, renderTemplate } from '../../components/utils';
import { useConfig } from '../../config';
import { useTranslation } from '../../i18n/i18n';
import { Res, useApi } from '../../state/api';
import { ApiCartItem, ApiDeliveryAddress, ApiList, ApiOrderItem } from '../../types';
import { AlertMessages, useAlerts } from '../AlertMessages/AlertMessages';
import { Badge } from '../Badge/Badge';
import { FormValidationContainer } from '../Form/FormValidatorContainer';
import { Loader } from '../Loader/Loader';
import './Subscription.css';
import { TitleEditInput } from './TitleEditInput';
import { EditScheduleAddress } from './editSchedule/EditScheduleAddress';
import { EditScheduleModal } from './editSchedule/EditScheduleModal';
import { DraftSteps } from './DraftSteps';
import { RenameSubscriptionModal } from './RenameSubscriptionModal';
import { RefillCost } from './RefillCost';
import { ReviewAndActivateModal } from './ReviewAndActivateModal';
import { UpcomingOrder } from './UpcomingOrder';
import { useSubscriptionWizard } from './useSubscriptionWizard';
import { ScheduleStatus } from '../../types';
import { useSession } from '../../state/session';
import { upcomingOrdersOf } from './utils';
import { canActivate, deliveryAddressOf, isDraft, statusOf } from './wizard';

type SubscriptionProps = {
	list: ApiList;
	updateSubscriptions: () => void;
	/* set for a subscription that was just named, so the wizard carries straight on to the rest */
	startWizard?: boolean;
	onWizardEnd?: () => void;
};

const SubscriptionContent = ({
	list,
	updateSubscriptions,
	startWizard,
	onWizardEnd,
}: SubscriptionProps): JSX.Element => {
	const { api } = useConfig();
	const translations = useTranslation();
	const t = { ...translations.subscriptions, cart: translations.cart, product: translations.product };
	const [loading, setLoading] = useState(false);
	const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState<string>('');
	const [renaming, setRenaming] = useState(false);
	const [reviewing, setReviewing] = useState(false);
	const [changingStatus, setChangingStatus] = useState(false);
	const alerts = useAlerts();
	const { user } = useSession();

	const { data: schedule, mutate: mutateSchedule, isLoading } = useApi(Res.schedule, { listId: list.id });

	const { step, wizardFor, start, pendingAddress, setPendingAddress } = useSubscriptionWizard({
		list,
		schedule,
		scheduleLoading: isLoading,
		startWizard,
		onWizardEnd,
		/*
		 * An address chosen for a subscription with no schedule is only ever written by the schedule
		 * step, so a wizard closed before that leaves a selection standing for nothing - and the
		 * card would show its summary as though the subscription had an address.
		 */
		onAbandoned: () => {
			if (!deliveryAddressOf(schedule)) setSelectedDeliveryAddress('');
		},
	});

	useEffect(() => {
		if (schedule?.deliveryAddress?.id !== undefined) {
			setSelectedDeliveryAddress(String(schedule.deliveryAddress.id));
		}
	}, [schedule?.deliveryAddress?.id]);

	const onAddProduct = async (item: ApiOrderItem): Promise<void> => {
		const productAlreadyAdded = list?.items.find((newItem) => newItem.product.id === item.product.id);
		const quantity = productAlreadyAdded ? (item.quantity += productAlreadyAdded.quantity) : item.quantity;

		await setQuantity(quantity, item.product.id);
	};

	const onRemoveProduct = async (item: ApiCartItem): Promise<void> => setQuantity(0, item.product.id);

	const onChangeProduct = async (item: ApiCartItem, quantity: number): Promise<void> =>
		setQuantity(quantity, item.product.id);

	const setQuantity = async (quantity: number, product: number) => {
		const payload = { list: list.id, product, quantity };

		alerts.clear();

		try {
			await axios.post(`${api}me/lists/set-quantity.json`, payload, WITH_CREDENTIALS);
		} catch (e) {
			alerts.set({ type: 'error', message: t.couldNotSave });
			return;
		}

		updateSubscriptions();
	};

	const draft = isDraft(schedule);
	const paused = statusOf(schedule) === 'paused';
	/*
	 * A paused subscription can have its last product taken out while it is stopped, so starting it
	 * again asks for the same three things activating a draft does rather than trusting what it was.
	 */
	const ready = canActivate(list, schedule);
	const ordersOnTheirWay = upcomingOrdersOf(schedule).length;

	/*
	 * Activating, pausing and starting again are the same write: the schedule as it stands, with a
	 * different word for what it is doing. The patch carries the whole schedule because that is the
	 * shape the api takes, and the status is the only thing that differs from what is already there.
	 */
	const setScheduleStatus = async (status: ScheduleStatus): Promise<boolean> => {
		if (!schedule) return false;

		alerts.clear();
		setChangingStatus(true);

		try {
			await axios.patch(
				`${api}me/lists/${list.id}/schedule.json`,
				{ ...schedule, deliveryAddressId: deliveryAddressOf(schedule)?.id, customerId: user?.id, status },
				WITH_CREDENTIALS
			);
		} catch (e) {
			alerts.set({ type: 'error', message: t.couldNotSave });
			return false;
		} finally {
			setChangingStatus(false);
		}

		mutateSchedule({ ...schedule, status }, true);

		return true;
	};

	const activate = async () => {
		if (await setScheduleStatus('active')) setReviewing(false);
	};

	const deleteList = async () => {
		// by its own name, so it is clear which one is going when a customer has several
		if (!window.confirm(renderTemplate(t.deleteListConfirm, { name: list.name }))) return;

		alerts.clear();
		setLoading(true);

		try {
			await axios.delete(`${api}me/lists/${list.id}.json`, WITH_CREDENTIALS);
		} catch (e) {
			alerts.set({ type: 'error', message: t.couldNotDelete });
			return;
		} finally {
			// without this a failed request left the button reading "Raderar lista..." for good
			setLoading(false);
		}

		updateSubscriptions();
	};

	/*
	 * Three states and nothing else in the badge: a draft while it is being built, paused when the
	 * customer has stopped it, and active when orders are being created. A draft says what it is
	 * waiting for in its own list of steps instead.
	 */
	const subscriptionStatus = draft
		? { label: t.draft, color: 'grey' as const }
		: paused
			? { label: t.paused, color: 'orange' as const }
			: { label: t.activeSchedule, color: 'green' as const };

	// the name, then what it is: "Din automatiska påfyllning · 2 av 5 steg klara" while it is a draft
	const stepsDone = 1 + [schedule, deliveryAddressOf(schedule), list.items.length].filter(Boolean).length;
	const subtitle = draft
		? `${t.yourAutomaticRefill} · ${renderTemplate(t.stepsDone, { done: stepsDone, total: 5 })}`
		: t.yourAutomaticRefill;

	const onUpdate = (address?: ApiDeliveryAddress) => {
		if (schedule && schedule.id !== undefined) {
			mutateSchedule({ ...schedule, deliveryAddress: address }, true);
		}
	};

	if (isLoading) {
		return (
			<ContentCard className="subscription-products">
				<div className="subscription-header">
					<div style={{ flex: 1 }}>
						<Loader size="line" />
					</div>
				</div>
				<div style={{ marginTop: '2rem' }}>
					<Loader size="grid" />
				</div>
			</ContentCard>
		);
	}

	return (
		<ContentCard className="subscription-products">
			<div className="subscription-header">
				<div className="subscription-title">
					{draft ? <h2>{list.name}</h2> : <TitleEditInput loading={loading} list={list} />}
					<p className="subscription-subtitle">{subtitle}</p>
				</div>

				<Badge size="small" color={subscriptionStatus.color}>
					{subscriptionStatus.label}
				</Badge>
			</div>
			{paused && (
				<p className="subscription-paused">
					{t.pausedNotice}
					{!ready && ` ${t.stepReviewWaiting}`}
				</p>
			)}
			{draft && (
				<DraftSteps
					list={list}
					schedule={schedule}
					loading={loading || changingStatus}
					onRename={() => setRenaming(true)}
					onStep={start}
					onReview={() => setReviewing(true)}
				/>
			)}
			{/* the order it has already created comes before what it is going to do next */}
			{!draft && schedule && <UpcomingOrder schedule={schedule} paused={paused} />}
			{/*
			 * The two things a running refill is set up with, side by side, each with the link that
			 * opens its dialog. A draft lists them as steps instead, so there the two components are
			 * nothing but their dialogs and the wrapper is not a grid.
			 */}
			<div className={draft ? undefined : 'schedule-settings'}>
				<EditScheduleModal
					deleting={loading}
					list={list}
					wizard={wizardFor('schedule')}
					pendingAddress={pendingAddress}
					dialogOnly={draft}
				/>
				{(schedule || step === 'address') && (
					<EditScheduleAddress
						list={list}
						schedule={schedule}
						onUpdate={onUpdate}
						setSelectedDeliveryAddress={setSelectedDeliveryAddress}
						selectedDeliveryAddress={selectedDeliveryAddress}
						wizard={wizardFor('address')}
						onAddressChosen={setPendingAddress}
						dialogOnly={draft}
					/>
				)}
			</div>
			<RenameSubscriptionModal
				list={list}
				show={renaming}
				onClose={() => setRenaming(false)}
				onRenamed={updateSubscriptions}
			/>
			<ReviewAndActivateModal
				list={list}
				schedule={schedule}
				show={reviewing}
				activating={changingStatus}
				alerts={alerts}
				onClose={() => setReviewing(false)}
				onActivate={activate}
			/>
			{!draft && (
				<div className="subscription-products-heading">
					<h3>{t.productsInRefill}</h3>
					{/* a created order is a thing of its own, and editing the refill touches none of them */}
					<p>
						{ordersOnTheirWay > 1
							? t.productChangesNoticeWithOrders
							: ordersOnTheirWay === 1
								? t.productChangesNoticeWithOrder
								: t.productChangesNotice}
					</p>
				</div>
			)}
			{Boolean(list.items.length) && (
				<subscription-items>
					<div className="not-mobile">
						<subscription-item-heading>
							<span>{t.cart.product}</span>
							<span>{t.product.artnr}</span>
							<span>{t.cart.amount}</span>
							<span className="text-right">{t.cart.total}</span>
						</subscription-item-heading>
					</div>
					{list.items.map((item) => (
						<SubscriptionListItem
							onDelete={onRemoveProduct}
							onChange={onChangeProduct}
							key={item.product.id}
							item={item}
						/>
					))}
				</subscription-items>
			)}
			{/* what it costs comes after what is in it, and only once it is running */}
			{!draft && <RefillCost list={list} />}
			{/* after the products, since adding one is a step that keeps the card a draft */}
			{draft && <p className="subscription-steps-footer">{t.draftFooter}</p>}
			<AlertMessages alerts={alerts} />
			<div className="subscription-footer">
				<AddProductModal
					deleting={loading}
					onSave={onAddProduct}
					title={t.addProductTitle}
					alerts={alerts}
					wizard={wizardFor('products', list.items.length > 0)}
					dialogOnly={draft}
				/>
				{/* a running subscription can be stopped without losing what it is made of */}
				{!draft && (
					<Button
						className="secondary"
						disabled={loading || changingStatus || (paused && !ready)}
						onClick={() => setScheduleStatus(paused ? 'active' : 'paused')}
					>
						{changingStatus ? (paused ? t.resuming : t.pausing) : paused ? t.resume : t.pause}
					</Button>
				)}
				<button type="button" className="text-link subscription-delete" disabled={loading} onClick={deleteList}>
					{!loading ? t.deleteList : t.deletingList}
				</button>
			</div>
		</ContentCard>
	);
};

export const Subscription = (props: SubscriptionProps): JSX.Element => (
	<FormValidationContainer.Provider>
		<SubscriptionContent {...props} />
	</FormValidationContainer.Provider>
);
